import cloneDeep from 'lodash/cloneDeep';
import type { Container } from 'pixi.js';
import { BoardTutorialView } from './BoardTutorialView';
import type {
  BoardTutorial,
  BoardTutorialCaptionCallback,
  BoardTutorialComponents,
  BoardTutorialMovement,
  BoardTutorialStep,
} from './types';
import { repeatUntilFalse } from '../../../utils/async-utils';
import MathUtils from '../../../utils/MathUtils';

interface BoardTutorialControllerConfig {
  parent: Container;
  maxWidth: number;
  maxHeight: number;
  onCaptionUpdate?: (caption: string) => void;
}

export class BoardTutorialController {
  private tutorialView?: BoardTutorialView;

  constructor(private readonly config: BoardTutorialControllerConfig) {}

  public playTutorial(tutorial: BoardTutorial, loop = true): this {
    if (this.tutorialView) {
      console.log('Error cannot run board tutorial sequence. A sequence is already active.');
      return this;
    }

    const { config } = this;
    const { parent, maxWidth, maxHeight, onCaptionUpdate } = config;

    const tutorialView = new BoardTutorialView({
      levelData: cloneDeep(tutorial.levelData),
      maxWidth,
      maxHeight,
    }).props({
      parent,
    });

    this.tutorialView = tutorialView;

    void (async (): Promise<void> => {
      await tutorialView.fadeIn();

      await this.executeTutorialSteps(tutorialView, tutorial.steps, onCaptionUpdate);

      await tutorialView.fadeOut();

      tutorialView.destroy();
      this.tutorialView = undefined;

      if (loop) this.playTutorial(tutorial, loop);
    })();
    return this;
  }

  private async executeTutorialSteps(
    tutorialView: BoardTutorialView,
    tutorialSteps: BoardTutorialStep[],
    setCaption?: BoardTutorialCaptionCallback,
  ): Promise<void> {
    for (let i = 0; i < tutorialSteps.length; i += 1) {
      const step = tutorialSteps[i];
      await this.executeStep(tutorialView, step, setCaption);
    }
  }

  private async executeStep(
    { components }: BoardTutorialView,
    tutorialStep: BoardTutorialStep,
    setCaption?: BoardTutorialCaptionCallback,
  ): Promise<void> {
    const { startDelay = 0, endDelay = 0, caption, movement } = tutorialStep;

    const { scheduler } = components;

    if (caption !== undefined && setCaption !== undefined) setCaption(caption);

    if (startDelay) await scheduler.wait(startDelay);

    if (movement) await this.executeHexMovement(components, movement);

    if (endDelay) await scheduler.wait(endDelay);
  }

  private async executeHexMovement(
    { gameAreaController, gameAreaView: { boardView, trayView } }: BoardTutorialComponents,
    movement: BoardTutorialMovement,
  ): Promise<void> {
    const item = trayView.getItemByIndex(movement.trayX);
    const toStack = boardView.getStackViewByGridXY(movement.stackX, movement.stackY);

    if (!item || !toStack) return;

    await repeatUntilFalse(() => toStack.model.locked);

    const stackView = item.stack;

    if (!stackView.parent || !toStack.parent) return;

    stackView.parent?.addChild(stackView);

    const toPos = stackView.parent.toLocal(toStack.position, toStack.parent);

    const duration = MathUtils.clamp(MathUtils.getDistance(stackView.x, stackView.y, toPos.x, toPos.y), 250, 500);

    await stackView.tweenTo({ x: toPos.x, y: toPos.y }, duration, 'quadOut', true).asyncOnComplete();

    await gameAreaController.putTileFromTray(item.stack, toStack);

    if (movement.waitUntilComplete) {
      await repeatUntilFalse(() => boardView.matchesAreAnimating);
    }
  }

  public update(deltaTime: number): void {
    this.tutorialView?.update(deltaTime);
  }
}
